import { isOpenAIAdsEnabled } from './client';
import logger, { isOpenAIAdsLoggingEnabled } from './logger';

declare global {
  interface Window {
    oaiq?: (
      command: string,
      eventName?: string,
      eventData?: Record<string, unknown>,
      eventOptions?: Record<string, unknown>
    ) => void;
  }
}

const supportedOpenAIAdsEvents = new Set([
  'appointment_scheduled',
  'checkout_started',
  'contents_viewed',
  'custom',
  'items_added',
  'lead_created',
  'order_created',
  'page_viewed',
  'registration_completed',
  'subscription_created',
  'trial_started',
]);

// OpenAI Ads expects monetary `amount` as an integer in the smallest currency unit (cents).
// Call sites pass amounts in major units that can be numeric strings (e.g. `convData.Paid`), so coerce before the single ×100 scale to avoid sending an un-normalised or string value.
const toCents = (amount: unknown): number | undefined => {
  const numericAmount = typeof amount === 'number' ? amount : Number(amount);
  if (!Number.isFinite(numericAmount)) {
    return undefined;
  }

  return Math.round(numericAmount * 100);
};

const normaliseEventData = (eventData: Record<string, unknown>): Record<string, unknown> => {
  if (!('amount' in eventData)) {
    return eventData;
  }

  const amountInCents = toCents(eventData.amount);
  if (amountInCents === undefined) {
    // Drop an unparseable amount rather than send the wrong monetary shape to OpenAI.
    const rest = { ...eventData };
    delete rest.amount;
    return rest;
  }

  return {
    ...eventData,
    amount: amountInCents,
  };
};

export const openAIAdsMeasure = (
  eventName: string,
  eventData: Record<string, unknown> = {},
  eventOptions: Record<string, unknown> = {}
) => {
  if (typeof window === 'undefined') {
    return;
  }

  const enabled = isOpenAIAdsEnabled();
  const oaiqReady = typeof window.oaiq === 'function';
  if (!enabled || !oaiqReady) {
    // Surface why a fired event did not reach OpenAI: consent withdrawn / env disabled (`enabled`) or the pixel stub never loaded (`oaiqReady`). Without this, skipped events log nothing.
    // Guarded so the context object is only allocated when logging is enabled.
    if (isOpenAIAdsLoggingEnabled) {
      logger.debug('measure skipped', { requestedEvent: eventName, enabled, oaiqReady });
    }
    return;
  }

  const normalisedEventData = normaliseEventData(eventData);
  const isSupportedEvent = supportedOpenAIAdsEvents.has(eventName);

  // Resolve the exact arguments handed to `oaiq` so logging reflects the real payload that OpenAI receives (supported events pass through; everything else is remapped to a `custom` event).
  const measureEventName = isSupportedEvent ? eventName : 'custom';
  const measureEventData = isSupportedEvent
    ? normalisedEventData
    : { ...normalisedEventData, type: 'custom' };
  const measureEventOptions = isSupportedEvent
    ? eventOptions
    : { ...eventOptions, custom_event_name: eventName };

  // Guarded so the payload object is only allocated when logging is enabled.
  if (isOpenAIAdsLoggingEnabled) {
    logger.debug('measure', {
      requestedEvent: eventName,
      payload: {
        command: 'measure',
        eventName: measureEventName,
        eventData: measureEventData,
        eventOptions: measureEventOptions,
      },
    });
  }

  window.oaiq('measure', measureEventName, measureEventData, measureEventOptions);
};
